import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  TriangleAlert,
} from "lucide-react";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { IssueDatePill } from "@/components/issues/issue-date-pill/issue-date-pill";
import { IssueStatusPill } from "@/components/issues/issue-status-pill/issue-status-pill";
import { IssueStatusTrigger } from "@/components/issues/issue-status-trigger/issue-status-trigger";
import { useStatusChangePulse } from "@/hooks/use-status-change-pulse";
import {
  formatIssueNumber,
  overdueLabel,
  personInitials,
  personName,
} from "@/lib/issues";
import type { IssueOrdering, IssueOrderingField } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import type { ProjectIssue, ProjectUser } from "@/types/api";
import { openIssueClick } from "@/components/issues/issue-item/open-issue-click";
import {
  IssuePickCircle,
  type IssueRowSelection,
} from "@/components/issues/issue-item/issue-pick-circle";

/**
 * Column track shared by the header and every row so they line up:
 * issue · raised by · assigned to · status · target date · due date · chevron.
 */
export const ISSUE_TABLE_GRID =
  "grid grid-cols-[minmax(0,2.8fr)_minmax(0,1.15fr)_minmax(0,1.15fr)_6.5rem_8.5rem_8.5rem_1.75rem] items-center gap-4";

/**
 * The same track with a leading 20px column for the pick circle, used by the
 * header and every row together while selecting — or the titles slide off
 * their columns. See [[issue-table-row.spec]].
 */
export const ISSUE_TABLE_GRID_SELECTING =
  "grid grid-cols-[1.25rem_minmax(0,2.8fr)_minmax(0,1.15fr)_minmax(0,1.15fr)_6.5rem_8.5rem_8.5rem_1.75rem] items-center gap-4";

const COLUMNS: ReadonlyArray<{
  label: string;
  field: IssueOrderingField;
}> = [
  { label: "Issue", field: "number" },
  { label: "Raised by", field: "created_by" },
  { label: "Assigned to", field: "assigned_to" },
  { label: "Status", field: "status" },
  { label: "Target date", field: "target_date" },
  { label: "Due date", field: "due_date" },
];

function nextOrdering(
  current: IssueOrdering,
  field: IssueOrderingField,
): IssueOrdering {
  if (current === field) return `-${field}`;
  if (current === `-${field}`) return field;
  return field;
}

interface IssueTableHeaderProps {
  /**
   * The current sort and its setter. Omit both for a list with a fixed order —
   * an issues report keeps the report's own — and the titles are plain labels.
   */
  ordering?: IssueOrdering;
  onOrderingChange?: (ordering: IssueOrdering) => void;
  /** Selection mode: an empty leading column over the rows' pick circles. */
  selecting?: boolean;
}

/** The column titles above the rows. See [[issue-table-row.spec]]. */
export function IssueTableHeader({
  ordering,
  onOrderingChange,
  selecting = false,
}: IssueTableHeaderProps) {
  return (
    <div
      data-slot="issue-table-header"
      className={cn(
        selecting ? ISSUE_TABLE_GRID_SELECTING : ISSUE_TABLE_GRID,
        "sticky top-0 z-10 bg-muted px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
      )}
    >
      {selecting && <span aria-hidden="true" />}
      {COLUMNS.map(({ label, field }) => {
        if (!ordering || !onOrderingChange) {
          return (
            <span key={field} className="min-w-0 truncate">
              {label}
            </span>
          );
        }
        const active = ordering === field || ordering === `-${field}`;
        const descending = ordering === `-${field}`;
        const Direction = active
          ? descending
            ? ArrowDown
            : ArrowUp
          : ArrowUpDown;
        return (
          <span
            key={field}
            className="min-w-0"
          >
            <button
              type="button"
              aria-label={
                active
                  ? `${label}, sorted ${descending ? "descending" : "ascending"}`
                  : `${label}, sort ascending`
              }
              onClick={() => onOrderingChange(nextOrdering(ordering, field))}
              className={cn(
                "group flex max-w-full cursor-pointer items-center gap-1 truncate text-left transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active && "text-foreground",
              )}
            >
              <span className="truncate">{label}</span>
              <Direction
                className={cn(
                  "h-3.5 w-3.5 shrink-0 transition-colors",
                  !active &&
                    "text-muted-foreground group-hover:text-foreground group-focus-visible:text-foreground",
                )}
                aria-hidden="true"
              />
            </button>
          </span>
        );
      })}
      <span aria-hidden="true" />
    </div>
  );
}

interface IssueTableRowProps {
  projectId: string;
  issue: ProjectIssue;
  /** Open the issue in place (desktop side panel); see [[issue-item.spec]]. */
  onOpen?: (issue: ProjectIssue) => void;
  /**
   * Open this issue's update sheet from the status pill, which becomes a
   * button. Omitted when this viewer could not post one. See
   * [[issue-status-trigger.spec]].
   */
  onUpdateStatus?: (issue: ProjectIssue) => void;
  /**
   * Selection mode: a leading pick circle, and the row toggles instead of
   * opening; the chevron and the status button go. See [[issue-table-row.spec]].
   */
  selection?: IssueRowSelection;
}

/**
 * One issue as a table row on wide screens — the desktop counterpart to
 * [[issue-item.spec]]. See [[issue-table-row.spec]].
 */
export function IssueTableRow({
  projectId,
  issue,
  onOpen,
  onUpdateStatus,
  selection,
}: IssueTableRowProps) {
  // Same fallback as the card: a broken thumbnail becomes the neutral tile.
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const thumbnail = thumbnailFailed ? null : issue.record_cover_thumbnail;

  const overdue = overdueLabel(issue);
  const needsAttention = issue.status === "completed";
  const dueShown = !!issue.due_date;
  const targetShown = !!issue.target_date;
  // The pill settles in when a refetch hands this row a new status. See
  // [[issue-item.spec]] US-005.
  const statusPulsing = useStatusChangePulse(issue.status);

  const cells = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        {thumbnail ? (
          // Same anonymous-CORS mode as the evidence strip and photo viewer,
          // which load this S3 thumbnail too; a no-CORS load here would poison
          // their cache entry. See [[s3-image-fetching]].
          <OfflineImage
            assetKey=""
            src={thumbnail}
            alt=""
            loading="lazy"
            onError={() => setThumbnailFailed(true)}
            className="h-12 w-12 shrink-0 rounded-md object-cover"
          />
        ) : (
          <div
            className="h-12 w-12 shrink-0 rounded-md bg-muted"
            aria-hidden="true"
          />
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-medium tracking-[-0.005em]">
            {formatIssueNumber(issue.number)} &mdash; {issue.title}
          </span>
          {issue.description && (
            <span className="truncate text-xs text-muted-foreground">
              {issue.description}
            </span>
          )}
        </div>
      </div>

      <div className="min-w-0">
        <PersonCell person={issue.created_by} emptyLabel="Unknown user" />
      </div>

      <div className="min-w-0">
        <PersonCell person={issue.assigned_to} emptyLabel="Unassigned" />
      </div>

      <div className="min-w-0">
        <span
          className={cn(
            "inline-flex shrink-0",
            statusPulsing && "status-pill-pulse",
          )}
        >
          {onUpdateStatus && !selection ? (
            <IssueStatusTrigger
              status={issue.status}
              onClick={() => onUpdateStatus(issue)}
            />
          ) : (
            <IssueStatusPill status={issue.status} />
          )}
        </span>
      </div>

      <div className="flex min-w-0 items-center">
        {targetShown ? (
          <IssueDatePill kind="target" value={issue.target_date} tinted />
        ) : (
          <span className="text-sm text-muted-foreground/70">—</span>
        )}
      </div>

      <div className="flex min-w-0 flex-col items-start gap-1">
        {dueShown ? (
          <IssueDatePill
            kind="due"
            value={issue.due_date}
            tinted
            overdue={!!overdue}
          />
        ) : (
          !overdue && <span className="text-sm text-muted-foreground/70">—</span>
        )}
        {/* The red the due pill turns, and the card's day count: one overdue
            colour across both shapes. */}
        {overdue && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-medium text-overdue">
            <TriangleAlert className="h-3 w-3" aria-hidden="true" />
            {overdue}
          </span>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        {needsAttention && (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-tertiary"
            aria-label="Waiting to be accepted"
          />
        )}
        {!selection && (
          <ChevronRight
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </div>
    </>
  );

  if (selection) {
    // A toggle, not a link — see the card's selection form in
    // [[issue-item.spec]] for why the fill is primary rather than muted.
    return (
      <button
        type="button"
        onClick={selection.onToggle}
        aria-pressed={selection.selected}
        className={cn(
          ISSUE_TABLE_GRID_SELECTING,
          "w-full px-3 py-2 text-left transition-colors duration-100",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          selection.selected
            ? "bg-primary/10"
            : "bg-card hover:bg-card-hover active:bg-card-pressed",
        )}
      >
        <IssuePickCircle selected={selection.selected} />
        {cells}
      </button>
    );
  }

  return (
    <Link
      to="/projects/$projectId/issues/$issueId"
      params={{ projectId, issueId: issue.id }}
      onClick={openIssueClick(issue, onOpen)}
      className={cn(
        ISSUE_TABLE_GRID,
        "bg-card px-3 py-2 transition-colors duration-100 hover:bg-card-hover active:bg-card-pressed",
      )}
    >
      {cells}
    </Link>
  );
}

function PersonCell({
  person,
  emptyLabel,
}: {
  person: ProjectUser | null;
  emptyLabel: string;
}) {
  if (!person) {
    return (
      <span className="truncate text-sm text-muted-foreground/70">
        {emptyLabel}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tertiary text-[10px] font-semibold text-tertiary-foreground"
        aria-hidden="true"
      >
        {personInitials(person)}
      </span>
      <span className="truncate text-sm">{personName(person)}</span>
    </span>
  );
}
